// @file Google Drive store
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import { GoogleDriveAuthorizationResult } from '@@/types'
import { JsonAPIResource } from '@padlet/arvo'
import { ApiErrorCode, SnackbarNotificationType } from 'enums'
import googleDriveColorSvg from 'images/google-drive-color.svg'
import { defineStore } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import PadletApi from 'surface/padlet_api'
import { computed, ref } from 'vue'
import type { RootState } from 'vuexstore/surface/types'

enum GoogleDriveFetchingStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

export const useGoogleDrivePickerStore = defineStore('googleDrivePicker', () => {
  // State
  const googleDriveFetchingStatus = ref(GoogleDriveFetchingStatus.Loading)
  const isUserLinkedWithGoogleDrive = computed(() => googleDriveEmail.value != null)
  const googleDriveEmail = ref<string | null>(null)
  const googleDriveAccessToken = ref<string | null>(null)
  const isGoogleDrivePickerOpen = ref<boolean>(false)

  // Getters

  const isFetchingGoogleDriveUserInformation = computed(
    () => googleDriveFetchingStatus.value === GoogleDriveFetchingStatus.Loading,
  )

  const isErroredFetchingGoogleDriveUserInformation = computed(
    () => googleDriveFetchingStatus.value === GoogleDriveFetchingStatus.Errored,
  )

  function setGoogleDriveEmail(email: string): void {
    googleDriveEmail.value = email
  }

  function setGoogleDriveAccessToken(token: string): void {
    googleDriveAccessToken.value = token
  }

  function setIsGoogleDrivePickerOpen(isOpen: boolean): void {
    isGoogleDrivePickerOpen.value = isOpen
  }

  async function fetchGoogleDriveInfo(): Promise<void> {
    const currentUser = getVuexStore<RootState>()?.getters.user
    try {
      googleDriveFetchingStatus.value = GoogleDriveFetchingStatus.Loading
      const response = await PadletApi.UserIntegrations.fetchGoogleDriveAuthorization(currentUser.id)
      const googleDriveInfo = (response.data as JsonAPIResource<GoogleDriveAuthorizationResult> | undefined)?.attributes
      if (googleDriveInfo != null) {
        googleDriveEmail.value = googleDriveInfo.linkedEmail
        googleDriveAccessToken.value = googleDriveInfo.accessToken
      }
      googleDriveFetchingStatus.value = GoogleDriveFetchingStatus.Completed
    } catch (e) {
      const error = JSON.parse(e.message)?.errors[0]
      if (error.code === ApiErrorCode.TOKEN_EXPIRED) {
        const refreshedResponse = await PadletApi.UserIntegrations.refreshGoogleDriveAccessToken(currentUser.id)

        const googleDriveInfo = (refreshedResponse?.data as JsonAPIResource<GoogleDriveAuthorizationResult>)?.attributes
        googleDriveEmail.value = googleDriveInfo.linkedEmail
        googleDriveAccessToken.value = googleDriveInfo.accessToken

        googleDriveFetchingStatus.value = GoogleDriveFetchingStatus.Completed
      } else {
        void useGlobalSnackbarStore().setSnackbar({
          message: __('Something went wrong. Please try again later.'),
          notificationType: SnackbarNotificationType.error,
        })
        googleDriveFetchingStatus.value = GoogleDriveFetchingStatus.Errored
      }
    }
  }

  function promptConnectWithGoogleDriveModal(): void {
    const currentUser = getVuexStore<RootState>()?.getters.user
    const payload = {
      iconSrc: googleDriveColorSvg,
      iconAlt: __('Google Drive icon'),
      title: __('Google Drive is not connected yet'),
      body: __(
        'Connect your Google account to import or link files from Google Drive. You will only have to do this once.',
      ),
      customBodyClasses: 'px-6',
      confirmButtonText: __('Connect'),
      cancelButtonText: __('Cancel'),
      afterConfirmActions: [
        {
          type: 'requestGoogleDriveAuthorizationFromSurface',
          payload: { userId: currentUser.id, tenantId: currentUser.tenant_id },
        },
      ],
      forceFullWidthButtons: true,
    }
    void getVuexStore<RootState>()?.dispatch('confirmationDialog/openConfirmationDialog', payload, { root: true })
  }

  return {
    // State
    isUserLinkedWithGoogleDrive,
    googleDriveEmail,
    googleDriveAccessToken,
    isGoogleDrivePickerOpen,

    // Getters
    isErroredFetchingGoogleDriveUserInformation,
    isFetchingGoogleDriveUserInformation,

    // Actions
    setGoogleDriveEmail,
    setGoogleDriveAccessToken,
    setIsGoogleDrivePickerOpen,
    fetchGoogleDriveInfo,
    promptConnectWithGoogleDriveModal,
  }
})
